import api from "./api"
import { AxiosError } from "axios"
import {
  SageCostCode,
  SageCostCodesResponse,
  SageDeleteResponse,
  SageHoursScope,
  SageJobCodeWrite,
  SageJobSite,
  SageJobSiteHoursDaysResponse,
  SageJobSiteHoursResponse,
  SageJobSitesResponse,
  SageJobSiteWrite,
  SageTodayRosterResponse,
  SageWorkDaysResponse,
} from "../Interfaces"
import { showErroPage } from "../utils/helper"
import { throwSageApiError } from "../utils/sageApiError"
import {
  aggregateHoursFromWorkDays,
  emptyHoursResponse,
  normalizeHoursResponse,
} from "../utils/jobSiteDailyHours"
import {
  emptyHoursDaysResponse,
  hoursDaysFromWorkDays,
  normalizeHoursDaysResponse,
} from "../utils/jobSiteHoursDays"

class SageJobSiteService {
  private baseUrl = "/sage-100/job-sites"

  private companyJobSitesUrl(companyId: number) {
    return `/sage-100/companies/${companyId}/job-sites`
  }

  private companyJobCodesUrl(companyId: number) {
    return `/sage-100/companies/${companyId}/job-codes`
  }

  public getAll = async (
    limit = 500,
    offset = 0,
    options?: { managementCompanyId?: number },
  ): Promise<SageJobSitesResponse> => {
    try {
      const params: { limit: number, offset: number, managementCompanyId?: number } = { limit, offset }
      if (options?.managementCompanyId) {
        params.managementCompanyId = options.managementCompanyId
      }
      const response = await api.get(this.baseUrl, { params })
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data)
        throw Error(error.response?.statusText)
      } else {
        console.error(error)
        showErroPage()
        return { success: false, sample: false, data: [], totalCount: 0 }
      }
    }
  }

  /**
   * Company-scoped job/cost codes. Prefers GET /sage-100/cost-codes?managementCompanyId=
   * and falls back to per-site cost-code lists (no Sage sync).
   */
  public getCostCodesByCompany = async (managementCompanyId: number): Promise<SageCostCodesResponse> => {
    if (!managementCompanyId) throw new Error("No management company id provided")
    try {
      const response = await api.get("/sage-100/cost-codes", {
        params: { managementCompanyId },
      })
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return this.getCostCodesByCompanyFallback(managementCompanyId)
      }
      if (error instanceof AxiosError) {
        console.error(error.response?.data)
        throw Error(error.response?.statusText)
      }
      throw error
    }
  }

  private getCostCodesByCompanyFallback = async (managementCompanyId: number): Promise<SageCostCodesResponse> => {
    const sites = await this.getAll(500, 0, { managementCompanyId })
    const data: SageCostCode[] = []
    for (const site of sites.data || []) {
      try {
        const codes = await this.getCostCodes(site.id)
        data.push(...(codes.data || []))
      } catch (siteError) {
        console.error(siteError)
      }
    }
    return {
      success: true,
      sample: Boolean(sites.sample),
      data,
      totalCount: data.length,
    }
  }

  public getCostCodes = async (jobSiteId: number): Promise<SageCostCodesResponse> => {
    if (!jobSiteId) throw new Error("No job site id provided")
    try {
      const response = await api.get(`${this.baseUrl}/${jobSiteId}/cost-codes`)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data)
        throw Error(error.response?.statusText)
      }
      console.error(error)
      throw error
    }
  }

  public getTodayRoster = async (jobSiteId: number): Promise<SageTodayRosterResponse> => {
    if (!jobSiteId) throw new Error("No job site id provided")
    try {
      const response = await api.get(`${this.baseUrl}/${jobSiteId}/roster/today`)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data)
        throw Error(error.response?.statusText)
      }
      console.error(error)
      throw error
    }
  }

  /**
   * SiteWalkAPI #129 hours contract.
   * Day: GET /sage-100/job-sites/:id/hours?scope=day&date=YYYY-MM-DD (omit date → today)
   * Project: GET /sage-100/job-sites/:id/hours?scope=project
   * Falls back to work-days if the endpoint is 404 until API #129 is merged.
   */
  public getHours = async (
    jobSiteId: number,
    options: { scope?: SageHoursScope, date?: string } = {},
  ): Promise<SageJobSiteHoursResponse> => {
    if (!jobSiteId) throw new Error("No job site id provided")
    const scope = options.scope === "project" ? "project" : "day"
    const date = scope === "day" && options.date ? options.date : undefined
    const params: { scope: SageHoursScope, date?: string } = { scope }
    if (date) params.date = date
    try {
      const response = await api.get(`${this.baseUrl}/${jobSiteId}/hours`, { params })
      return normalizeHoursResponse(response.data, jobSiteId, date ?? null, "hours-api", scope)
    } catch (error) {
      if (error instanceof AxiosError) {
        const status = error.response?.status
        if (status === 404) {
          return this.getHoursFallback(jobSiteId, scope, date)
        }
        console.error(error.response?.data)
        throw Error(error.response?.statusText)
      }
      console.error(error)
      throw error
    }
  }

  public getDailyHours = async (jobSiteId: number, date: string): Promise<SageJobSiteHoursResponse> => {
    return this.getHours(jobSiteId, { scope: "day", date })
  }

  /**
   * SiteWalkAPI #130:
   * GET /sage-100/job-sites/:id/hours/days?from=YYYY-MM-DD&to=YYYY-MM-DD
   * Response `{ dates }` — ISO days with totalHours > 0. Falls back to work-days on 404.
   */
  public getHoursDays = async (
    jobSiteId: number,
    options: { from: string, to: string },
  ): Promise<SageJobSiteHoursDaysResponse> => {
    if (!jobSiteId) throw new Error("No job site id provided")
    const { from, to } = options
    try {
      const response = await api.get(`${this.baseUrl}/${jobSiteId}/hours/days`, {
        params: { from, to },
      })
      return normalizeHoursDaysResponse(response.data, jobSiteId, from, to, "hours-days-api")
    } catch (error) {
      if (error instanceof AxiosError) {
        const status = error.response?.status
        if (status === 404) {
          return this.getHoursDaysFallback(jobSiteId, from, to)
        }
        console.error(error.response?.data)
        throw Error(error.response?.statusText)
      }
      console.error(error)
      throw error
    }
  }

  public getWorkDays = async (jobSiteId: number, date?: string): Promise<SageWorkDaysResponse> => {
    if (!jobSiteId) throw new Error("No job site id provided")
    const params: { jobSiteId: number, date?: string, limit: number } = { jobSiteId, limit: 500 }
    if (date) params.date = date
    const response = await api.get("/sage-100/work-days", { params })
    return response.data
  }

  private getHoursDaysFallback = async (
    jobSiteId: number,
    from: string,
    to: string,
  ): Promise<SageJobSiteHoursDaysResponse> => {
    try {
      const workDays = await this.getWorkDays(jobSiteId)
      return hoursDaysFromWorkDays(workDays?.data, jobSiteId, from, to, Date.now())
    } catch (fallbackError) {
      console.error(fallbackError)
      return emptyHoursDaysResponse(jobSiteId, from, to, "unavailable")
    }
  }

  private getHoursFallback = async (
    jobSiteId: number,
    scope: SageHoursScope,
    date?: string,
  ): Promise<SageJobSiteHoursResponse> => {
    try {
      const workDays = await this.getWorkDays(jobSiteId, scope === "project" ? undefined : date)
      return aggregateHoursFromWorkDays(workDays?.data, jobSiteId, date ?? null, Date.now(), scope)
    } catch (fallbackError) {
      console.error(fallbackError)
      return emptyHoursResponse(jobSiteId, date ?? null, "unavailable", scope)
    }
  }

  public createJobSite = async (companyId: number, body: SageJobSiteWrite): Promise<SageJobSite> => {
    if (!companyId) throw new Error("No management company id provided")
    try {
      const response = await api.post(this.companyJobSitesUrl(companyId), body)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not add the job site.")
    }
  }

  public updateJobSite = async (
    companyId: number,
    jobSiteId: number,
    body: Partial<SageJobSiteWrite>,
  ): Promise<SageJobSite> => {
    if (!companyId) throw new Error("No management company id provided")
    if (!jobSiteId) throw new Error("No job site id provided")
    try {
      const response = await api.patch(`${this.companyJobSitesUrl(companyId)}/${jobSiteId}`, body)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not update the job site.")
    }
  }

  public deleteJobSite = async (companyId: number, jobSiteId: number): Promise<SageDeleteResponse> => {
    if (!companyId) throw new Error("No management company id provided")
    if (!jobSiteId) throw new Error("No job site id provided")
    try {
      const response = await api.delete(`${this.companyJobSitesUrl(companyId)}/${jobSiteId}`)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not delete the job site.")
    }
  }

  public createCostCode = async (companyId: number, body: SageJobCodeWrite): Promise<SageCostCode> => {
    if (!companyId) throw new Error("No management company id provided")
    try {
      const response = await api.post(this.companyJobCodesUrl(companyId), body)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not add the job code.")
    }
  }

  public updateCostCode = async (
    companyId: number,
    costCodeId: number,
    body: Partial<SageJobCodeWrite>,
  ): Promise<SageCostCode> => {
    if (!companyId) throw new Error("No management company id provided")
    if (!costCodeId) throw new Error("No job code id provided")
    try {
      const response = await api.patch(`${this.companyJobCodesUrl(companyId)}/${costCodeId}`, body)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not update the job code.")
    }
  }

  public deleteCostCode = async (companyId: number, costCodeId: number): Promise<SageDeleteResponse> => {
    if (!companyId) throw new Error("No management company id provided")
    if (!costCodeId) throw new Error("No job code id provided")
    try {
      const response = await api.delete(`${this.companyJobCodesUrl(companyId)}/${costCodeId}`)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not delete the job code.")
    }
  }
}

export const sageJobSiteService = new SageJobSiteService()
